import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import { SxProps } from '@mui/system';
import React from 'react';

import appleStoreBadge from '../../images/apple-store-badge.svg';
import cellphones from '../../images/cellphones.png';
import googlePlayBadge from '../../images/google-play-badge.png';
import playerAppBannerBackgroundTile from '../../images/player-app-banner-background-tile.png';
import analytics from '../analytics';

export interface PlayerAppBannerProps {
    appleStoreLink: string | 'DISABLED';
    className?: string;
    googlePlayStoreLink: string | 'DISABLED';
    onDismiss: () => void;
}

const CloseButton = ({ onClick, sx = {} }: { onClick: () => void; sx?: SxProps | undefined }) => (
    <Button
        aria-label="dismiss player app banner"
        size="large"
        title="Dismiss Player App Banner"
        variant="contained"
        onClick={onClick}
        sx={{
            backgroundColor: grey[900],
            minWidth: '0',
            width: '40px',
            height: '40px',
            padding: '0',
            fontSize: '24px',
            '&:hover': {
                backgroundColor: grey[900],
            },
            ...sx,
        }}
    >
        <CloseIcon fontSize="inherit" />
    </Button>
);
const PlayerAppBanner: React.FC<PlayerAppBannerProps> = ({
    appleStoreLink,
    className = '',
    googlePlayStoreLink,
    onDismiss,
}) => {
    const handleClose = () => {
        analytics.logPlayerAppBannerDismissed();
        onDismiss();
    };
    return (
        <Stack
            className={[className, 'ddbcl-player-app-banner'].filter(Boolean).join(' ')}
            alignItems="center"
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 0 }}
            sx={{
                padding: '16px 24px',
                minHeight: '50px',
                position: 'relative',
                overflow: 'hidden',
                backgroundRepeat: 'repeat',
                backgroundSize: '84px',
                backgroundImage: `url(${playerAppBannerBackgroundTile})`,
                width: '100%',
                margin: '0 auto 16px',
            }}
        >
            <Stack direction={'row'} sx={{ width: '100%' }}>
                <Box sx={{ flex: '0 0 40px', display: { xs: 'flex', md: 'none' } }} />
                <Typography
                    variant="h4"
                    color="#fff"
                    sx={{ flex: 1, fontSize: { xs: '24px', sm: '30px' }, textAlign: { xs: 'center', md: 'left' } }}
                >
                    Play Anywhere, Anytime
                </Typography>
                <CloseButton onClick={handleClose} sx={{ flex: '0 0 40px', display: { xs: 'flex', md: 'none' } }} />
            </Stack>
            <Box
                aria-label="3 cellphones with dnd"
                sx={{
                    position: 'absolute',
                    bottom: '0px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundImage: `url(${cellphones})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '100%',
                    width: '200px',
                    height: '70px',
                    display: { xs: 'none', md: 'block' },
                }}
            />
            <Stack
                spacing={2}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'flex-end'}
                flexGrow={1}
                sx={{ margin: 0 }}
            >
                {appleStoreLink && appleStoreLink !== 'DISABLED' && (
                    <Box
                        component={'a'}
                        href={appleStoreLink}
                        target="_blank"
                        onClick={() => analytics.logPlayerAppBannerClickedCta('Apple')}
                        sx={{
                            display: 'inline-block',
                            width: '120px',
                            height: '40px',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'contain',
                            backgroundImage: `url(${appleStoreBadge})`,
                        }}
                        aria-label="Download the D&amp;D Beyond Player App on the Apple App Store"
                    />
                )}
                {googlePlayStoreLink && googlePlayStoreLink !== 'DISABLED' && (
                    <Box
                        component={'a'}
                        href={googlePlayStoreLink}
                        target="_blank"
                        onClick={() => analytics.logPlayerAppBannerClickedCta('Google')}
                        sx={{
                            display: 'inline-block',
                            width: '134px', // Wider than apple so they have the same visual height
                            height: '40px',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'contain',
                            backgroundImage: `url(${googlePlayBadge})`,
                        }}
                        aria-label="Download the D&amp;D Beyond Player App on Google Play"
                    />
                )}
                <CloseButton onClick={handleClose} sx={{ display: { xs: 'none', md: 'flex' } }} />
            </Stack>
        </Stack>
    );
};

export default PlayerAppBanner;
